import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Clock, Star, MapPin, ShieldCheck } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { LiveBadge } from "../components/LiveBadge";
import { CityNoVendorOptions } from "../components/CityNoVendorOptions";
import { GuidesBlock } from "../components/GuidesBlock";
import { useSeoTitle } from "../context/SeoContext";
import { MIN_REVIEWS } from "../context/CityContext";

function JsonLd({ json }) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = json;
    document.head.appendChild(el);
    return () => { document.head.removeChild(el); };
  }, [json]);
  return null;
}

const FAQ = (city) => [
  { q: `La livraison de CBD à ${city} est-elle légale ?`, a: `Oui. Tous les produits STUFF CBD sont issus de chanvre légal avec un taux de THC inférieur à 0,3%, conformément à la réglementation française et européenne. Chaque produit est analysé en laboratoire et la vente est réservée aux personnes majeures (18+).` },
  { q: `En combien de temps suis-je livré à ${city} ?`, a: `Nos vendeurs locaux livrent généralement en moins de 2h quand ils sont en ligne. Si aucun vendeur n'est disponible, tu peux recevoir ta commande par colis en 48h via notre boutique nationale.` },
  { q: `Comment payer ma commande CBD à ${city} ?`, a: `Aucun paiement en ligne : tu règles directement à la livraison, en toute simplicité. Tu finalises ta commande avec le vendeur via WhatsApp ou Snapchat.` },
  { q: `Quels produits CBD peut-on se faire livrer à ${city} ?`, a: `Fleurs CBD premium, résines et hash, e-liquides et puffs, chicha CBD et accessoires : tout le catalogue STUFF CBD est disponible à la livraison à ${city}. Chaque produit affiche son analyse laboratoire et son taux de THC (<0,3%).` },
  { q: `Quelle zone est desservie autour de ${city} ?`, a: `Chaque vendeur local dessert un rayon autour de ${city} (affiché sur sa boutique). Les frais de livraison démarrent à 3,50€ (2 premiers km inclus) et la livraison peut être offerte dès un certain montant ou avec le Club STUFF.` },
  { q: `Faut-il créer un compte pour commander du CBD à ${city} ?`, a: `Non. Tu choisis tes produits, tu indiques ton adresse à ${city}, et tu confirmes avec le vendeur sur WhatsApp ou Snapchat. C'est simple, rapide et sans inscription obligatoire.` },
];

const POURQUOI = (city) => [
  ["🚀 Livraison express locale", `Un vendeur STUFF CBD près de chez toi à ${city} prépare et livre ta commande en main propre, souvent en moins de 2h. Fini l'attente des colis de 3 jours.`],
  ["🌿 Qualité contrôlée", `Chanvre 100% légal (<0,3% THC), analyses laboratoire consultables sur chaque fiche produit. La même qualité dans toutes les villes, au même prix.`],
  ["💶 Paiement cash à la livraison", `Zéro paiement en ligne : tu payes ton CBD à la réception, en espèces ou selon l'arrangement avec ton vendeur à ${city}.`],
  ["🎁 Fidélité & Club", `Cagnotte fidélité, codes promo locaux et Club STUFF pour la livraison offerte : commander ton CBD à ${city} te rapporte à chaque fois.`],
];

const LOCAL_INTRO = {
  "Évreux": "Du centre-ville d'Évreux à La Madeleine en passant par Nétreville et Saint-Michel, ton livreur STUFF sillonne la préfecture de l'Eure. Fini les détours par Rouen ou Paris pour trouver du CBD de qualité : la capitale ébroïcienne est désormais servie en direct.",
  "Rouen": "Rive droite ou rive gauche, du Vieux-Marché à Saint-Sever en passant par les Hauts-de-Rouen et Sotteville, STUFF livre toute la métropole rouennaise. La capitale normande mérite du CBD premium sans attendre le colis : commande, et ton livreur traverse la Seine pour toi.",
  "Cergy-Pontoise": "De Cergy-Préfecture aux Trois-Fontaines, de Pontoise à Éragny, Osny ou Vauréal, l'agglo de Cergy-Pontoise est couverte. Étudiant du campus ou riverain de l'Axe Majeur, ton CBD arrive directement dans le 95 — sans monter sur Paris.",
  "Les Mureaux": "Aux Mureaux et dans toute la vallée de Seine — Meulan, Verneuil, Flins — ton livreur STUFF connaît le terrain. À deux pas de Mantes, le 78 s'agrandit : fleurs, résines et puffs livrés en direct, sans passer par la case bureau de tabac.",
  "Amiens": "De Saint-Leu à Henriville, de la Hotoie au quartier Saint-Pierre, STUFF couvre la capitale picarde. Étudiant de la Citadelle ou riverain des Hortillonnages, ton CBD arrive en direct — plus besoin de guetter un colis depuis Paris ou Lille.",
  "Bruxelles": "D'Ixelles à Saint-Gilles, de Schaerbeek à Uccle en passant par le centre, STUFF débarque dans la capitale belge. Fleurs, résines et puffs livrés en main propre bruxelloise — commande en ligne, ton livreur traverse la ville pour toi.",
  "Le Havre": "Du centre Perret à Sanvic, de Caucriauville à Sainte-Adresse, ton livreur STUFF connaît chaque rue de la cité Océane. Le CBD premium arrive enfin au Havre en direct — sans détour par Rouen, sans attendre le facteur.",
  "Liège": "Des Guillemins à Outremeuse, du Carré à Sainte-Marguerite, STUFF livre la Cité Ardente. Le CBD de qualité arrive en principauté : commande, et ton livreur remonte la Meuse jusqu'à ta porte.",
  "Bernay": "Au cœur de la vallée de Charentonne, du centre historique aux Granges, Bernay a son livreur STUFF. Entre Évreux et Lisieux, plus besoin de faire la route : le CBD premium arrive directement dans ta ville normande.",
  "Sarcelles": "Du Grand Ensemble au Village, de la gare de Garges-Sarcelles aux Flanades, STUFF quadrille le 95. Le CBD légal arrive à Sarcelles en direct — fini les allers-retours sur Paris pour trouver de la qualité.",
  "Saint Ouen": "Des Puces aux Docks, du Vieux Saint-Ouen à la mairie desservie par la ligne 14, STUFF livre Saint-Ouen-sur-Seine. Aux portes de Paris, ton CBD arrive plus vite que ton métro — commande et reste posé.",
  "95 Val d’Oise": "D'Argenteuil à Cergy, de Sarcelles à Garges en passant par Ermont et Franconville, STUFF couvre tout le Val-d'Oise. Où que tu sois dans le 95, un livreur connaît ton secteur : fleurs, résines et puffs en direct.",
  "Hagondange": "Dans la vallée de l'Orne, entre Metz et Thionville, Hagondange a rejoint le réseau STUFF. De la gare au Ballastière, ton CBD arrive en direct en Moselle — sans passer par le Luxembourg ni attendre un colis.",
  "Bourgoin-Jallieu": "Du champ de Mars à Pré Bénit, Bourgoin-Jallieu et le Nord-Isère sont couverts. Entre Lyon et Grenoble, plus besoin de monter sur la métropole : ton livreur STUFF sillonne la Berjallie avec du CBD premium.",
  "Sint-Jans-Molenbeek": "Le long du canal, du vieux Molenbeek au Karreveld, STUFF livre Sint-Jans-Molenbeek et l'ouest bruxellois. Le CBD légal arrive en direct dans la commune — commande en ligne, remise en main propre.",
};

const slugify = (name) => name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function CityLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [otherCities, setOtherCities] = useState([]);
  const [reviews, setReviews] = useState([]);
  const otherCityLinks = useMemo(
    () => otherCities.filter((c) => c.name !== (data?.city || "")).slice(0, 12),
    [otherCities, data]
  );

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/landing/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
    api.get("/cities").then((r) => setOtherCities(r.data)).catch(console.error);
    api.get("/reviews/highlights?limit=3").then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(console.error);
  }, [slug]);

  const city = data?.city || "";
  useSeoTitle("city", { ville: city }, !!city);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-anton text-4xl uppercase text-white mb-4">Ville introuvable</h1>
        <Link to="/" className="text-neon-green uppercase font-semibold">← Choisir ma ville</Link>
      </div>
    );
  }
  if (!data) return <p className="text-zinc-500 text-center py-20">Chargement...</p>;

  const open = data.live_count > 0;
  const faq = FAQ(city);
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        "mainEntity": faq.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
      },
      {
        "@type": "LocalBusiness",
        "name": `STUFF CBD ${city}`,
        "description": `Livraison rapide de CBD à ${city} : fleurs, résines, e-liquides, puffs, chicha. Chanvre légal <0,3% THC. Paiement cash à la livraison.`,
        "areaServed": { "@type": "City", "name": city },
        "priceRange": "€€",
        "url": typeof window !== "undefined" ? window.location.href : "",
        ...(data.avg_rating > 0 && data.rating_count >= MIN_REVIEWS ? { "aggregateRating": { "@type": "AggregateRating", "ratingValue": data.avg_rating, "reviewCount": data.rating_count } } : {}),
      },
    ],
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="city-landing">
      <JsonLd json={JSON.stringify(jsonLd)} />
      <p className="text-neon-magenta font-graffiti text-2xl">Livraison express</p>
      <h1 className="font-anton text-4xl sm:text-6xl uppercase text-white leading-none mb-4">
        Livraison CBD <span className="neon-text-green">{city}</span>
      </h1>
      <p className="text-zinc-300 max-w-3xl mb-6">
        Commande tes produits CBD préférés à {city} — fleurs premium, résines, e-liquides, puffs et chicha —
        et fais-toi livrer <span className="text-neon-green font-semibold">en moins de 2h</span> par un vendeur local.
        Chanvre 100% légal (&lt;0,3% THC), analysé en laboratoire. Paiement cash à la livraison, sans compte à créer.
      </p>
      {(data.local_intro || LOCAL_INTRO[city]) && (
        <p data-testid="landing-local-intro" className="text-zinc-400 max-w-3xl mb-6 border-l-2 border-neon-magenta/60 pl-4 whitespace-pre-line">
          {data.local_intro || LOCAL_INTRO[city]}
        </p>
      )}

      {data.vendors.length === 0 && <CityNoVendorOptions city={city} waitingCount={data.waiting_count || 0} />}

      {/* Statut */}
      {data.vendors.length > 0 && <div data-testid="landing-status" className={`flex items-center gap-3 border px-5 py-3 mb-8 ${open ? "border-neon-green/50 bg-neon-green/5" : "border-red-500/50 bg-red-500/5"}`}>
        {open ? (
          <>
            <span className="relative flex h-3 w-3 shrink-0"><span className="animate-ping absolute h-full w-full rounded-full bg-neon-green opacity-75" /><span className="relative rounded-full h-3 w-3 bg-neon-green" /></span>
            <p className="text-neon-green font-bold uppercase text-sm">Livraison ouverte à {city} — {data.live_count} vendeur{data.live_count > 1 ? "s" : ""} en ligne</p>
          </>
        ) : (
          <>
            <span className="h-3 w-3 rounded-full bg-red-500 shrink-0" />
            <p className="text-red-400 font-bold uppercase text-sm">Aucun vendeur en ligne pour le moment — précommande ou colis 48h possible</p>
          </>
        )}
      </div>}

      {/* Chiffres clés */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
        <div className="bg-ink-surface border border-zinc-800 p-4 text-center"><div className="font-anton text-3xl text-neon-green">{data.vendors.length}</div><div className="text-zinc-500 text-xs uppercase">boutique{data.vendors.length > 1 ? "s" : ""} à {city}</div></div>
        <div className="bg-ink-surface border border-zinc-800 p-4 text-center"><div className="font-anton text-3xl text-neon-magenta">&lt; 2h</div><div className="text-zinc-500 text-xs uppercase">délai moyen</div></div>
        {data.vendors.length === 0 && data.waiting_count > 0 ? (
          <div className="bg-ink-surface border border-neon-magenta/40 p-4 text-center" data-testid="landing-waiting-stat"><div className="font-anton text-3xl text-neon-magenta">{data.waiting_count}</div><div className="text-zinc-500 text-xs uppercase">en attente d'un livreur</div></div>
        ) : (
          data.rating_count >= MIN_REVIEWS ? (
            <div className="bg-ink-surface border border-zinc-800 p-4 text-center" data-testid="landing-rating-stat"><div className="font-anton text-3xl text-neon-green">{`${data.avg_rating}/5`}</div><div className="text-zinc-500 text-xs uppercase">{data.rating_count} avis clients</div></div>
          ) : (
            <div className="bg-ink-surface border border-neon-magenta/40 p-4 text-center" data-testid="landing-new-vendor-stat"><div className="font-anton text-2xl text-neon-magenta leading-tight">✨ Nouveau<br />vendeur</div><div className="text-zinc-500 text-xs uppercase">avis vérifiés à venir</div></div>
          )
        )}
        <div className="bg-ink-surface border border-zinc-800 p-4 text-center"><div className="font-anton text-3xl text-neon-magenta">{data.max_radius_km > 0 ? `${Math.round(data.max_radius_km)} km` : "48h"}</div><div className="text-zinc-500 text-xs uppercase">{data.max_radius_km > 0 ? "zone desservie" : "par colis"}</div></div>
      </div>

      {/* Vendeurs */}
      <h2 className="font-anton text-2xl uppercase text-white mb-4">Les boutiques CBD à {city}</h2>
      {data.vendors.length === 0 ? (
        <p className="text-zinc-500 text-sm mb-10" data-testid="landing-no-vendor-note">Aucune boutique locale pour l'instant — utilise le colis 48h ou active l'alerte ci-dessus. 👆</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {data.vendors.map((v) => (
            <button key={v.id} data-testid="landing-vendor-card" onClick={() => navigate(`/boutique/${v.id}`)} className="flex items-center gap-4 bg-ink-surface border border-zinc-800 p-5 text-left hover:border-neon-magenta transition-all">
              <div className="h-14 w-14 rounded-full overflow-hidden border-2 border-zinc-700 shrink-0 bg-black flex items-center justify-center">
                {v.avatar_url ? <img src={imageUrl(v.avatar_url)} alt={v.name} className="h-full w-full object-cover" /> : <span className="font-anton text-xl text-neon-green">{v.name?.[0]?.toUpperCase()}</span>}
              </div>
              <div className="min-w-0">
                <h3 className="font-anton text-lg uppercase text-white truncate">{v.name}</h3>
                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-zinc-500">
                  <LiveBadge live={v.is_live} />
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {v.delivery_time || "< 2h"}</span>
                  {v.opening_hours && <span>🕑 {v.opening_hours}</span>}
                  {v.rating_count >= MIN_REVIEWS ? <span className="flex items-center gap-1 text-neon-green font-bold"><Star className="h-3 w-3 fill-neon-green" /> {v.rating} ({v.rating_count})</span> : <span data-testid="new-vendor-badge" className="text-[10px] uppercase font-bold border border-neon-magenta text-neon-magenta px-1.5 py-0.5">✨ Nouveau vendeur</span>}
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {Math.round(v.radius_km)} km</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Réassurance */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border border-neon-green/30 bg-neon-green/5 px-5 py-3 mb-10 text-sm text-zinc-300">
        <span className="flex items-center gap-2 font-bold text-neon-green"><ShieldCheck className="h-4 w-4" /> Chanvre légal &lt;0,3% THC</span>
        <span>🔬 Produits analysés en laboratoire</span>
        <span>💶 Paiement cash à la livraison</span>
        <span>🔞 Réservé aux 18 ans et +</span>
      </div>

      {/* Pourquoi STUFF à {city} — contenu SEO */}
      <h2 className="font-anton text-2xl uppercase text-white mb-4">Pourquoi commander ton CBD à {city} avec STUFF ?</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10" data-testid="landing-why">
        {POURQUOI(city).map(([t2, x]) => (
          <div key={t2} className="bg-ink-surface border border-zinc-800 p-5">
            <h3 className="font-anton text-base uppercase text-neon-green mb-1.5">{t2}</h3>
            <p className="text-zinc-400 text-sm">{x}</p>
          </div>
        ))}
      </div>

      {/* Produits livrés — contenu SEO */}
      <h2 className="font-anton text-2xl uppercase text-white mb-3">Quels produits CBD livrés à {city} ?</h2>
      <p className="text-zinc-400 text-sm mb-4 max-w-3xl">
        Toutes les boutiques STUFF CBD de {city} proposent le même catalogue contrôlé, au même prix : <span className="text-white">fleurs CBD premium</span> (indoor, greenhouse), <span className="text-white">résines et hash</span>, <span className="text-white">e-liquides et puffs CBD</span> pour vapoter, <span className="text-white">chicha CBD</span> et accessoires. Chaque fiche produit affiche l'analyse laboratoire et le taux de THC (&lt;0,3%). Livraison à domicile, au bureau ou en point de rendez-vous partout à {city} et alentours.
      </p>
      <div className="flex flex-wrap gap-2 mb-10">
        {["Fleurs CBD", "Résines & Hash", "E-liquides", "Puffs", "Chicha CBD"].map((cat) => (
          <span key={cat} className="text-xs uppercase font-bold text-neon-magenta border border-neon-magenta/40 px-3 py-1.5">{cat} {city}</span>
        ))}
      </div>

      {/* FAQ SEO */}
      <h2 className="font-anton text-2xl uppercase text-white mb-4">Questions fréquentes — CBD à {city}</h2>
      <div className="space-y-4 mb-12">
        {FAQ(city).map((f) => (
          <details key={f.q} className="bg-ink-surface border border-zinc-800 p-5 group">
            <summary className="font-semibold text-white cursor-pointer list-none flex justify-between items-center">{f.q}<span className="text-neon-green group-open:rotate-45 transition-transform text-xl">+</span></summary>
            <p className="text-zinc-400 text-sm mt-3">{f.a}</p>
          </details>
        ))}
      </div>

      <div className="text-center pb-4">
        <Link to={`/ville/${encodeURIComponent(city)}`} data-testid="landing-order-cta" className="inline-block bg-neon-magenta text-white font-anton uppercase text-lg px-10 py-4 glow-magenta hover:bg-neon-purple transition-colors">
          Commander à {city} maintenant
        </Link>
      </div>

      {/* Avis vérifiés clients */}
      {reviews.length > 0 && (
        <div className="border-t border-zinc-900 pt-8 pb-8" data-testid="landing-reviews">
          <p className="text-neon-green text-xs uppercase font-bold tracking-widest mb-1">Retours d'expérience</p>
          <h2 className="font-anton text-2xl uppercase text-white mb-5">Avis vérifiés <span className="neon-text-magenta">clients</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {reviews.map((r) => (
              <div key={r.id} className="bg-ink-surface border border-zinc-800 p-4" data-testid="landing-review-card">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white text-sm font-semibold">✓ {r.author}</span>
                  <span className="text-neon-green border border-neon-green/40 text-[9px] uppercase font-bold px-1.5 py-0.5">Achat vérifié</span>
                </div>
                <div className="flex gap-0.5 mb-2">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "text-neon-green fill-[#9DFF00]" : "text-zinc-700"}`} />)}
                </div>
                <p className="text-zinc-400 text-sm">« {r.text} »</p>
                {r.photo_url && <img src={imageUrl(r.photo_url)} alt="Photo client" className="mt-2 h-16 w-16 object-cover border border-zinc-700" loading="lazy" />}
                <p className="text-zinc-600 text-[11px] mt-2">{r.product_name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Maillage interne SEO — autres villes */}
      <GuidesBlock city={city} />

      {otherCityLinks.length > 0 && (
        <div className="border-t border-zinc-900 pt-6 pb-8" data-testid="landing-other-cities">
          <h2 className="text-zinc-500 text-xs uppercase font-bold mb-3">Livraison CBD dans d'autres villes</h2>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {otherCityLinks.map((c) => (
              <Link key={c.name} to={`/livraison-cbd/${slugify(c.name)}`} className="text-zinc-400 text-sm hover:text-neon-green transition-colors">CBD {c.name}</Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
